/* global window, document */

import { useCallback, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { BRAND_LOGO_URL } from '@/data/servicesData';

const BRAND_BLUE = "#1c669d";
const GOOGLE_ADS_ID = "AW-17953212580";

const getGoogleAdsScriptSrc = (googleAdsId) =>
  `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`;

const buildLinkClickPayload = ({ googleAdsId, anchor }) => {
  const href = anchor.getAttribute("href");
  const linkText = (anchor.textContent || "").trim();

  return {
    event: "blackline_link_click",
    send_to: googleAdsId,
    event_category: "navigation",
    event_label: linkText || href,
    link_url: href,
    page_path: window.location.pathname,
  };
};

const isTrackableHref = (href) =>
  Boolean(href && !href.startsWith("#") && !href.startsWith("javascript:"));

export default function GlobalHead() {
  const localBusinessJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'BLACKLINE CLEANING',
    url: 'https://www.blackline-cleaning.co.za',
    image: BRAND_LOGO_URL,
    telephone: ['+27690579693'],
    email: 'services@blackline-cleaning.co.za',
    areaServed: ['Johannesburg', 'Sandton'],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Johannesburg',
      addressRegion: 'Gauteng',
      addressCountry: 'ZA',
    },
    sameAs: ['https://www.blackline-cleaning.co.za'],
  }), []);

  const initializeGoogleAds = useCallback(() => {
    if (window.__blacklineGoogleAdsInitialized) return;
    window.__blacklineGoogleAdsInitialized = true;

    const src = getGoogleAdsScriptSrc(GOOGLE_ADS_ID);
    const existing = document.querySelector(`script[src="${src}"]`);
    if (!existing) {
      const script = document.createElement("script");
      script.async = true;
      script.src = src;
      document.head.appendChild(script);
    }

    // Init dataLayer + gtag function (safe even before gtag.js loads)
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };

    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_ADS_ID);
  }, []);

  useEffect(() => {
    initializeGoogleAds();
  }, [initializeGoogleAds]);

  const handleGlobalLinkClick = useCallback((event) => {
    const anchor = event.target.closest("a");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!isTrackableHref(href)) return;

    const payload = buildLinkClickPayload({ googleAdsId: GOOGLE_ADS_ID, anchor });

    if (typeof window.gtag === "function") {
      window.gtag("event", "blackline_link_click", {
        ...payload,
        transport_type: "beacon",
      });
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleGlobalLinkClick, true);

    return () => {
      document.removeEventListener("click", handleGlobalLinkClick, true);
    };
  }, [handleGlobalLinkClick]);

  return (
    <Helmet>
      {/* Browser UI theme color */}
      <meta name="theme-color" content={BRAND_BLUE} />
      <meta name="msapplication-TileColor" content={BRAND_BLUE} />
      <meta name="apple-mobile-web-app-status-bar-style" content={BRAND_BLUE} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />

      {/* Favicons / App icons */}
      <link rel="icon" href={BRAND_LOGO_URL} sizes="any" />
      <link rel="icon" type="image/jpeg" sizes="48x48" href={BRAND_LOGO_URL} />
      <link rel="icon" type="image/jpeg" sizes="32x32" href={BRAND_LOGO_URL} />
      <link rel="icon" type="image/jpeg" sizes="16x16" href={BRAND_LOGO_URL} />
      <link rel="apple-touch-icon" href={BRAND_LOGO_URL} />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="preconnect" href="https://customer-assets.emergentagent.com" />
      <link rel="preconnect" href="https://images.unsplash.com" />
      <link rel="dns-prefetch" href="https://customer-assets.emergentagent.com" />
      <link rel="dns-prefetch" href="https://images.unsplash.com" />

      {/* Safari pinned tab */}
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color={BRAND_BLUE} />

      <script type="application/ld+json">
        {JSON.stringify(localBusinessJsonLd)}
      </script>
    </Helmet>
  );
}
